"use client";
import StyledContainer from "@/components/StyledContainer";
import {StyledPrismicRichText} from "@/components/StyledPrismicRichText";
import clsx from "clsx";
import {PrismicNextLink} from "@prismicio/next";
import Breadcrumbs from "@/components/Breadcrumbs";
import {useEffect, useState} from "react";

/**
 * @typedef {import("@prismicio/client").Content.RichTextSlice} RichTextSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<RichTextSlice>} RichTextProps
 * @param {RichTextProps}
 */
const RichText = ({ slice }) => {
  const [breadcrumbs, setBreadcrumbs] = useState({ parent: null, child: null });

  useEffect(() => {
    const capitalizeWords = (text) => {
      return text
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const path = window?.location?.pathname || ""; // Ensure the path is properly defined
    const segments = path.split("/").filter((segment) => segment);

    const checkPath = () => {
      if (segments[0] === "accident-lawyer-in-new-york")
        return "accident-lawyer-in-new-york";
    };

    switch (checkPath()) {
      case "accident-lawyer-in-new-york":
        if (segments.length === 1) {
          setBreadcrumbs({
            parent: {
              link: "/accident-lawyer-in-new-york",
              label: "Accident Lawyer In New York",
            },
            child: null, // No child breadcrumb for the main page
          });
        } else if (segments.length > 1) {
          // Handle sub-paths under /practice-areas
          setBreadcrumbs({
            parent: {
              link: "/accident-lawyer-in-new-york",
              label: "Accident Lawyer In New York",
            },
            child: {
              link: path,
              label: capitalizeWords(
                segments[segments.length - 1].replace(/-/g, " "),
              ),
            },
          });
        }
        break;

      default:
        setBreadcrumbs({ parent: null, child: null });
        break;
    }
  }, []);
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <StyledContainer slice={slice}>
        <div
          className={clsx({
            "max-w-[800px] mx-auto": !slice.primary.full_width,
          })}
        >
          <div className="hidden md:block absolute top-[-40px] md:top-[-60px] lg:mt-[0px] lg:absolute lg:top-[-60px] left-[0px]">
            {breadcrumbs.parent && (
              <Breadcrumbs
                parent={breadcrumbs.parent}
                child={breadcrumbs.child}
                dark={true}
              />
            )}
          </div>
          <div className={``}>
            <StyledPrismicRichText
              field={slice.primary.content}
              components={{
                heading1: ({ children }) => (
                  <h1 className="title-xl title-font first:mt-0 mb-4 last:mb-0">
                    {children}
                  </h1>
                ),
                heading2: ({ children }) => (
                  <h2 className="title-xl title-font first:mt-0 last:mb-0">
                    {children}
                  </h2>
                ),
                heading3: ({ children }) => (
                  <h3 className="title-xl title-font first:mt-0 last:mb-0">
                    {children}
                  </h3>
                ),
                heading4: ({ children }) => (
                  <h4 className="title-large title-font first:mt-0 last:mb-0">
                    {children}
                  </h4>
                ),
                heading5: ({ children }) => (
                  <h5 className="title-medium title-font first:mt-0 last:mb-0">
                    {children}
                  </h5>
                ),
                heading6: ({ children }) => (
                  <h6 className="mb-5 leading-6 title-small">{children}</h6>
                ),
                paragraph: ({ children }) => (
                  <p className="mb-7 mt-4 last:mb-0">{children}</p>
                ),
                oList: ({ children }) => (
                  <ol className="pl-4 mb-7 last:mb-0 md:pl-6">{children}</ol>
                ),
                oListItem: ({ children }) => (
                  <li className="pl-1 mb-1 list-decimal last:mb-0 md:pl-2">
                    {children}
                  </li>
                ),
                list: ({ children }) => (
                  <ul className="pl-4 mb-7 last:mb-0 md:pl-6">{children}</ul>
                ),
                listItem: ({ children }) => (
                  <li className="pl-1 mb-1 list-disc last:mb-0 md:pl-2">
                    {children}
                  </li>
                ),
                preformatted: ({ children }) => (
                  <pre className="p-4 text-sm rounded mb-7 bg-slate-100 last:mb-0 md:p-8 md:text-lg">
                    <code>{children}</code>
                  </pre>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
                hyperlink: ({ children, node }) => (
                  <PrismicNextLink
                    field={node.data}
                    className="underline decoration-1 underline-offset-2"
                  >
                    {children}
                  </PrismicNextLink>
                ),
              }}
            />
          </div>
        </div>
      </StyledContainer>
    </section>
  );
};

export default RichText;
